import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { shadow, shadowMd } from '../../../../web/styles/shadows';

/**
 * Styles for the support ticket composer. These stay on the support inbox theme tokens so the
 * composer matches the surrounding admin support app.
 */

const BUBBLE_ICON_COLOR = THEME.colors.background.light;
const BUBBLE_HOVER_BACKGROUND = 'rgba(255, 255, 255, 0.14)';
const BUBBLE_DIVIDER_COLOR = 'rgba(255, 255, 255, 0.18)';
const BUBBLE_ACTIVE_BACKGROUND = THEME.colors.primary.light;
const BUBBLE_ACTIVE_COLOR = THEME.colors.primary.main;

const MENU_ROW_ACTIVE_BACKGROUND = THEME.colors.primary.light;
const MENU_ROW_HOVER_BACKGROUND = THEME.colors.background.main;
const MENU_ITEM_BORDER_RADIUS = THEME.common.borderRadius / 4;

const menuPopup = {
	position: 'absolute',
	zIndex: 40,
	background: THEME.colors.background.light,
	border: `1px solid ${THEME.colors.border.light}`,
	borderRadius: THEME.common.borderRadius,
	boxShadow: shadowMd,
	overflow: 'hidden',
} as const;

// components-v2 Button drops the outline on :focus; without a ring, Tab into the bar looks like a no-op.
const keyboardFocusRing = {
	outline: `2px solid ${THEME.colors.primary.main}`,
	outlineOffset: 2,
} as const;

const iconButton = {
	border: 'none',
	background: 'transparent',
	padding: 0,
	color: THEME.colors.text.light,
	cursor: 'pointer',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	':hover': {
		color: THEME.colors.text.main,
	},
	':focus-visible': keyboardFocusRing,
} as const;

const barButton = {
	width: 28,
	height: 28,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	border: 'none',
	background: 'transparent',
	borderRadius: THEME.common.borderRadius,
	color: THEME.colors.icon.gray,
	cursor: 'pointer',
	fontFamily: 'inherit',
	fontSize: 14,
	padding: 0,
	':hover': {
		background: MENU_ROW_HOVER_BACKGROUND,
		color: THEME.colors.text.main,
	},
	':focus-visible': keyboardFocusRing,
} as const;

const bubbleButton = {
	width: 27,
	height: 27,
	border: 'none',
	background: 'transparent',
	borderRadius: THEME.common.borderRadius,
	color: BUBBLE_ICON_COLOR,
	fontFamily: 'inherit',
	fontSize: 13,
	cursor: 'pointer',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 0,
	':hover': {
		background: BUBBLE_HOVER_BACKGROUND,
	},
} as const;

const textButton = {
	border: 'none',
	background: 'transparent',
	color: THEME.colors.text.light,
	fontFamily: 'inherit',
	fontSize: 13,
	cursor: 'pointer',
	padding: 0,
	':hover': {
		color: THEME.colors.text.main,
	},
} as const;

export const composerStyles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: THEME.spacing.sm,
		fontFamily: THEME.typography.fontFamilies.sora,
	},

	// Floating entry points over the conversation timeline while the composer is collapsed.
	collapsedActions: {
		position: 'absolute',
		left: '50%',
		bottom: THEME.spacing.md,
		transform: 'translateX(-50%)',
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.xs,
		zIndex: 2,
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadow,
	},

	// Open composer card.
	card: {
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		// Tracks the visual viewport (see useComposerViewportFit) so the keyboard cannot push the
		// header and send bar off screen. Custom properties, because Aphrodite marks every
		// declaration !important and an inline style could not win otherwise.
		'@media (max-width: 767px)': {
			position: 'fixed',
			left: 0,
			right: 0,
			top: 'var(--composer-viewport-top, 0px)',
			height: 'var(--composer-viewport-height, 100dvh)',
			zIndex: 60,
			border: 'none',
			borderRadius: 0,
		},
	},
	headerRow: {
		display: 'flex',
		alignItems: 'flex-end',
		gap: 22,
		padding: '12px 22px 0',
	},
	tabs: {
		display: 'flex',
		gap: 22,
	},
	tab: {
		border: 'none',
		background: 'transparent',
		padding: '0 1px 9px',
		fontFamily: 'inherit',
		fontSize: 14,
		fontWeight: 400,
		color: THEME.colors.text.light,
		borderBottom: '2px solid transparent',
		cursor: 'pointer',
		':hover': {
			color: THEME.colors.text.main,
		},
	},
	tabActive: {
		fontWeight: 600,
		color: THEME.colors.text.main,
		borderBottomColor: THEME.colors.primary.main,
	},
	tabActiveNote: {
		borderBottomColor: THEME.colors.info.main,
	},
	headerSpacer: {
		flex: 1,
	},
	headerButtons: {
		display: 'flex',
		gap: 14,
		paddingBottom: 9,
	},
	headerIconBtn: iconButton,
	// Phone exit: a 44px labelled target instead of a 20px glyph in the corner.
	headerCancelBtn: {
		flex: 'none',
		height: 44,
		padding: `0 ${THEME.spacing.sm}px`,
		fontSize: THEME.typography.fontSizes.sm,
	},
	hairline: {
		height: 1,
		background: THEME.colors.border.light,
	},

	// Status dot rows (sending disabled, internal note, customerless).
	noticeRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '9px 22px',
		fontSize: 12.5,
		color: THEME.colors.text.main,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	noticeDot: {
		width: 7,
		height: 7,
		borderRadius: '50%',
		flexShrink: 0,
	},
	noticeDotWarning: {
		background: THEME.colors.warning.main,
	},
	noticeAction: {
		height: 'auto',
		padding: 0,
		fontFamily: 'inherit',
		fontSize: 'inherit',
	},

	// To / Cc / Bcc rows.
	addressFields: {
		display: 'flex',
		flexDirection: 'column',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	addressRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '10px 22px',
	},
	addressSubRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '0 22px 10px',
	},
	addressLabel: {
		fontSize: 13,
		color: THEME.colors.text.light,
		flexShrink: 0,
	},
	addressValue: {
		fontSize: 13,
		color: THEME.colors.text.main,
		flex: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	addressInput: {
		flex: 1,
		border: 'none',
		outline: 'none',
		fontFamily: 'inherit',
		fontSize: 13,
		color: THEME.colors.text.main,
		background: 'transparent',
		padding: '2px 0',
		borderRadius: 0,
		height: 'auto',
		boxShadow: 'none',
		'@media (max-width: 767px)': { fontSize: 16 },
	},
	addressToggleBtn: {
		...textButton,
		fontSize: 12,
		padding: '2px 4px',
		whiteSpace: 'nowrap',
		':hover': {
			color: THEME.colors.primary.main,
		},
	},

	// Recipient typeahead, anchored under the address it completes.
	recipientField: {
		position: 'relative',
		display: 'flex',
		flex: 1,
		minWidth: 0,
	},
	recipientMenu: {
		...menuPopup,
		top: '100%',
		left: 0,
		marginTop: THEME.spacing.xs,
		width: 320,
		maxWidth: '100%',
		maxHeight: 260,
		overflowY: 'auto',
		padding: 5,
	},
	recipientItem: {
		gap: 1,
		minHeight: 40,
	},
	recipientItemName: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		minWidth: 0,
	},

	// Formatting bar (from the base composer variant).
	formattingBar: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		padding: '6px 14px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		position: 'relative',
		flexWrap: 'wrap',
		'@media (max-width: 767px)': { flexWrap: 'nowrap', overflowX: 'auto', flexShrink: 0 },
	},
	barBtn: barButton,
	barBtnActive: {
		background: MENU_ROW_ACTIVE_BACKGROUND,
		color: THEME.colors.primary.main,
	},
	barBtnWide: {
		...barButton,
		width: 'auto',
		padding: '0 8px',
		gap: 4,
		fontSize: 13,
		fontWeight: 600,
	},
	barBtnBold: {
		fontWeight: 700,
	},
	barBtnItalic: {
		fontFamily: 'Georgia, serif',
		fontStyle: 'italic',
		fontSize: 15,
	},
	barBtnUnderline: {
		textDecoration: 'underline',
		textUnderlineOffset: 2,
	},
	barDivider: {
		width: 1,
		height: 18,
		background: THEME.colors.border.light,
		margin: '0 6px',
	},
	barGlyphStack: {
		display: 'inline-flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 1,
	},
	barGlyphLetter: {
		fontSize: 13,
		fontWeight: 600,
		lineHeight: 1,
	},
	barGlyphSwatch: {
		width: 14,
		height: 3,
		borderRadius: 2,
	},
	sizePopover: {
		...menuPopup,
		top: 38,
		left: 62,
		zIndex: 30,
		padding: 4,
		display: 'flex',
		flexDirection: 'column',
		minWidth: 130,
	},
	sizeOption: {
		border: 'none',
		background: 'transparent',
		textAlign: 'left',
		fontFamily: 'inherit',
		color: THEME.colors.text.main,
		padding: '7px 10px',
		borderRadius: 7,
		cursor: 'pointer',
		':hover': {
			background: MENU_ROW_HOVER_BACKGROUND,
		},
	},
	colorPopover: {
		...menuPopup,
		top: 38,
		left: 150,
		zIndex: 30,
		padding: 8,
		display: 'flex',
		gap: 6,
	},
	colorSwatch: {
		width: 22,
		height: 22,
		borderRadius: '50%',
		border: '1px solid rgba(0, 0, 0, 0.12)',
		cursor: 'pointer',
		padding: 0,
		':hover': {
			transform: 'scale(1.12)',
		},
	},

	// Editor body.
	editorWrap: {
		position: 'relative',
		'@media (max-width: 767px)': { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
	},
	editorScroll: {
		overflowY: 'auto',
		minHeight: 140,
		maxHeight: 300,
		cursor: 'text',
		'@media (max-width: 767px)': { flex: 1, minHeight: 0, maxHeight: 'none' },
	},
	editorScrollExpanded: {
		minHeight: 340,
		maxHeight: 540,
	},

	// Dark bubble menu shown on text selection.
	bubbleMenu: {
		zIndex: 40,
		background: THEME.colors.text.main,
		borderRadius: 9,
		boxShadow: '0 8px 24px rgba(13, 15, 17, 0.28)',
		display: 'flex',
		alignItems: 'center',
		gap: 1,
		padding: 3,
	},
	bubbleBtn: bubbleButton,
	bubbleBtnActive: {
		background: BUBBLE_ACTIVE_BACKGROUND,
		color: BUBBLE_ACTIVE_COLOR,
	},
	bubbleBtnBold: {
		fontWeight: 700,
	},
	bubbleBtnItalic: {
		fontFamily: 'Georgia, serif',
		fontStyle: 'italic',
		fontSize: 14,
	},
	bubbleBtnUnderline: {
		textDecoration: 'underline',
		textUnderlineOffset: 2,
	},
	bubbleBtnRefine: {
		...bubbleButton,
		width: 'auto',
		padding: '0 9px',
		gap: 5,
		fontSize: 12.5,
		fontWeight: 600,
		color: THEME.colors.background.light,
	},
	bubbleDivider: {
		width: 1,
		height: 15,
		background: BUBBLE_DIVIDER_COLOR,
		margin: '0 3px',
	},

	// Link popover.
	linkPopover: {
		...menuPopup,
		zIndex: 45,
		borderRadius: 9,
		padding: 5,
		display: 'flex',
		gap: 5,
		alignItems: 'center',
		maxWidth: `calc(100% - ${THEME.spacing.md}px)`,
		boxSizing: 'border-box',
		overflow: 'visible',
	},
	linkPreviewText: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		minWidth: 0,
		paddingLeft: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.xs,
		whiteSpace: 'nowrap',
	},
	linkPreviewUrl: {
		display: 'inline-block',
		maxWidth: 260,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: THEME.colors.primary.main,
	},
	linkInput: {
		width: 200,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 6,
		padding: '6px 9px',
		fontFamily: 'inherit',
		fontSize: 13,
		color: THEME.colors.text.main,
		outline: 'none',
		':focus': {
			borderColor: THEME.colors.primary.main,
		},
	},
	linkApplyBtn: {
		height: 29,
		padding: '0 11px',
		border: 'none',
		borderRadius: 6,
		background: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		fontFamily: 'inherit',
		fontSize: 12.5,
		fontWeight: 600,
		cursor: 'pointer',
		':hover': {
			background: THEME.colors.hover.main,
			color: THEME.colors.text.main,
		},
	},

	// Slash (saved replies) and mention menus.
	suggestionMenu: {
		...menuPopup,
		width: 320,
	},
	suggestionMenuMention: {
		width: 320,
	},
	suggestionMenuHeader: {
		padding: '9px 13px 6px',
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: THEME.colors.text.light,
	},
	suggestionMenuList: {
		display: 'flex',
		flexDirection: 'column',
		padding: '0 5px 5px',
		maxHeight: 225,
		overflowY: 'auto',
	},
	suggestionMenuListMention: {
		padding: 5,
		maxHeight: 220,
	},
	suggestionItem: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		gap: 2,
		height: 'auto',
		border: 'none',
		textAlign: 'left',
		background: 'transparent',
		borderRadius: MENU_ITEM_BORDER_RADIUS,
		padding: '7px 9px',
		cursor: 'pointer',
		width: '100%',
		boxSizing: 'border-box',
		fontFamily: 'inherit',
		lineHeight: 1.25,
		':hover': {
			background: MENU_ROW_HOVER_BACKGROUND,
		},
	},
	suggestionItemActive: {
		background: MENU_ROW_HOVER_BACKGROUND,
	},
	suggestionItemDisabled: {
		opacity: 0.45,
		cursor: 'default',
	},
	suggestionItemMention: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		minHeight: 44,
		padding: '6px 8px',
	},
	suggestionItemName: {
		fontSize: 13,
		fontWeight: 600,
		color: THEME.colors.text.main,
	},
	suggestionItemSub: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: '100%',
	},
	mentionAvatar: {
		width: 32,
		height: 32,
		flexShrink: 0,
	},
	mentionText: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: 1,
		minWidth: 0,
	},
	suggestionMenuEmpty: {
		padding: '12px 9px',
		fontSize: 13,
		color: THEME.colors.text.light,
	},
	suggestionMenuFooter: {
		padding: '6px 13px',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		fontSize: 10.5,
		color: THEME.colors.text.light,
	},

	// AI assist panel (thinking dots / suggestion preview).
	aiPanel: {
		borderTop: `1px solid ${THEME.colors.border.light}`,
		padding: '13px 22px',
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		// Lets the suggestion, not the send bar, give way when the phone card runs out of room.
		minHeight: 0,
	},
	aiPanelHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
	},
	aiPanelLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: THEME.colors.text.light,
	},
	aiThinkingDots: {
		display: 'flex',
		gap: 5,
		padding: '4px 0 2px',
	},
	aiPanelBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
		minHeight: 0,
	},
	aiPreview: {
		fontSize: 14,
		lineHeight: 1.6,
		color: THEME.colors.text.main,
		maxHeight: 'min(240px, 30vh)',
		overflowY: 'auto',
		overscrollBehavior: 'contain',
	},
	aiPanelActions: {
		display: 'flex',
		gap: 14,
		alignItems: 'center',
		flexShrink: 0,
	},
	aiAcceptBtn: {
		height: 30,
		padding: '0 13px',
		border: 'none',
		borderRadius: 7,
		background: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		fontFamily: 'inherit',
		fontSize: 12.5,
		fontWeight: 600,
		cursor: 'pointer',
		':hover': {
			background: THEME.colors.hover.main,
			color: THEME.colors.text.main,
		},
	},
	aiDismissBtn: {
		...textButton,
		fontSize: 12.5,
	},

	// Pending attachments.
	attachmentList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		padding: '0 22px 12px',
	},
	attachmentRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: 3,
		maxWidth: 320,
	},
	attachmentRowMain: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	attachmentName: {
		fontSize: 13,
		color: THEME.colors.text.main,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	attachmentMeta: {
		fontSize: 11,
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
	},
	attachmentRemoveBtn: iconButton,
	attachmentProgress: {
		height: 2,
		borderRadius: 1,
		background: THEME.colors.primary.main,
		width: '40%',
		animationName: {
			'0%': { width: '8%' },
			'100%': { width: '92%' },
		},
		animationDuration: '1.2s',
		animationIterationCount: 'infinite',
		animationDirection: 'alternate',
		animationTimingFunction: 'ease-in-out',
	},

	// Footer send row.
	footer: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: 16,
		padding: '13px 22px 16px',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		position: 'relative',
		'@media (max-width: 767px)': {
			position: 'sticky',
			bottom: 0,
			zIndex: 2,
			paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
			backgroundColor: THEME.colors.background.light,
		},
	},
	sendBtn: {
		height: 32,
		padding: '0 18px',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		fontFamily: 'inherit',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		whiteSpace: 'nowrap',
		flexShrink: 0,
		cursor: 'pointer',
		':hover': {
			color: THEME.colors.primary.contrastText,
		},
		':active': {
			color: THEME.colors.primary.contrastText,
		},
		':disabled': {
			opacity: 0.45,
			pointerEvents: 'none',
		},
	},
	sendCloseBtn: {
		height: 32,
		padding: '0 18px',
		fontFamily: 'inherit',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		whiteSpace: 'nowrap',
		flexShrink: 0,
		color: THEME.colors.text.main,
		':hover': {
			color: THEME.colors.text.main,
		},
		':active': {
			color: THEME.colors.text.main,
		},
		':disabled': {
			opacity: 0.45,
			pointerEvents: 'none',
		},
	},
	footerSpacer: {
		flex: 1,
	},
	draftLabel: {
		fontSize: 11,
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	footerIcons: {
		display: 'flex',
		gap: 12,
		alignItems: 'center',
		order: 1,
	},
	footerIconBtn: iconButton,
	footerAiBtn: {
		order: 1,
	},
	footerIconBtnActive: {
		color: THEME.colors.primary.main,
	},
	aiMenu: {
		...menuPopup,
		bottom: 50,
		right: 18,
		left: 'auto',
		zIndex: 50,
		width: 240,
		padding: 5,
		display: 'flex',
		flexDirection: 'column',
	},
	aiMenuItem: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		gap: 1,
		height: 'auto',
		border: 'none',
		textAlign: 'left',
		background: 'transparent',
		borderRadius: MENU_ITEM_BORDER_RADIUS,
		padding: '7px 10px',
		cursor: 'pointer',
		fontFamily: 'inherit',
		':hover': {
			background: MENU_ROW_HOVER_BACKGROUND,
		},
		':disabled': {
			opacity: 0.5,
			pointerEvents: 'none',
		},
	},
	aiMenuItemLabel: {
		fontSize: 13,
		fontWeight: 600,
		color: THEME.colors.text.main,
	},
	aiMenuItemDesc: {
		fontSize: 11,
		color: THEME.colors.text.light,
	},
	fileInput: {
		display: 'none',
	},

	// pointer-events none so the overlay never steals the drop or churns dragenter/dragleave;
	// the drop lands on the composer underneath (or on the editor for position-aware drops).
	dropOverlay: {
		position: 'absolute',
		inset: 0,
		zIndex: 5,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		background: 'rgba(255, 255, 255, 0.82)',
		border: `2px dashed ${THEME.colors.primary.main}`,
		borderRadius: THEME.common.borderRadius,
		pointerEvents: 'none',
	},
	dropOverlayLabel: {
		fontSize: 13,
		fontWeight: 500,
		color: THEME.colors.primary.main,
	},
});
